import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import all translation files
import en from './locales/en/translation.json';
import es from './locales/es/translation.json';
import zh from './locales/zh/translation.json';
import fr from './locales/fr/translation.json';
import ar from './locales/ar/translation.json';
import he from './locales/he/translation.json';
import ru from './locales/ru/translation.json';
import pt from './locales/pt/translation.json';
import hi from './locales/hi/translation.json';
import bn from './locales/bn/translation.json';
import ur from './locales/ur/translation.json';
import tl from './locales/tl/translation.json';
import pl from './locales/pl/translation.json';
import ko from './locales/ko/translation.json';
import ja from './locales/ja/translation.json';
import it from './locales/it/translation.json';
import de from './locales/de/translation.json';
import ht from './locales/ht/translation.json';
import yo from './locales/yo/translation.json';
import sw from './locales/sw/translation.json';

// RTL languages
export const RTL_LANGUAGES = ['ar', 'he', 'ur'];

// All supported languages with metadata
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Espanol', dir: 'ltr' },
  { code: 'zh', name: 'Chinese', nativeName: '\u4e2d\u6587', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Francais', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', dir: 'rtl' },
  { code: 'he', name: 'Hebrew', nativeName: '\u05e2\u05d1\u05e8\u05d9\u05ea', dir: 'rtl' },
  { code: 'ru', name: 'Russian', nativeName: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Portugues', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: '\u0939\u093f\u0928\u094d\u0926\u0940', dir: 'ltr' },
  { code: 'bn', name: 'Bengali', nativeName: '\u09ac\u09be\u0982\u09b2\u09be', dir: 'ltr' },
  { code: 'ur', name: 'Urdu', nativeName: '\u0627\u0631\u062f\u0648', dir: 'rtl' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', dir: 'ltr' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '\ud55c\uad6d\uc5b4', dir: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '\u65e5\u672c\u8a9e', dir: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr' },
  { code: 'ht', name: 'Haitian Creole', nativeName: 'Kreyol Ayisyen', dir: 'ltr' },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yoruba', dir: 'ltr' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', dir: 'ltr' },
] as const;

const resources = {
  en: { translation: en },
  es: { translation: es },
  zh: { translation: zh },
  fr: { translation: fr },
  ar: { translation: ar },
  he: { translation: he },
  ru: { translation: ru },
  pt: { translation: pt },
  hi: { translation: hi },
  bn: { translation: bn },
  ur: { translation: ur },
  tl: { translation: tl },
  pl: { translation: pl },
  ko: { translation: ko },
  ja: { translation: ja },
  it: { translation: it },
  de: { translation: de },
  ht: { translation: ht },
  yo: { translation: yo },
  sw: { translation: sw },
};

// Register before init so it catches the initial language detection
i18n.on('languageChanged', (lng: string) => {
  const dir = RTL_LANGUAGES.includes(lng) ? 'rtl' : 'ltr';
  document.documentElement.dir = dir;
  document.documentElement.lang = lng;
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: true, // XSS protection - React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'roosting_language',
    },
  });

export default i18n;
